import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { translations, DEFAULT_LOCALE } from "../i18n";

// ─────────────────────────────────────────────
// Context definition
// ─────────────────────────────────────────────
const AppContext = createContext(null);

/**
 * AppProvider — wraps the whole app, exposes:
 *   theme       : "dark" | "light"
 *   toggleTheme : function
 *   locale      : "en" | "fr"
 *   setLocale   : function
 *   t           : translation accessor (dot-path string or object key)
 */
export function AppProvider({ children }) {
  // ── Theme ──────────────────────────────────
  const [theme, setTheme] = useState(() => {
    // Persist user preference in localStorage
    const saved = localStorage.getItem("portfolio-theme");
    if (saved) return saved;
    // Respect OS preference as default
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      localStorage.setItem("portfolio-theme", next);
      return next;
    });
  }, []);

  // Apply theme class to <html> so CSS variables kick in globally
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // ── Locale ─────────────────────────────────
  const [locale, setLocaleState] = useState(() => {
    const saved = localStorage.getItem("portfolio-locale");
    if (saved && translations[saved]) return saved;
    // Detect browser language
    const browserLang = navigator.language.slice(0, 2);
    return translations[browserLang] ? browserLang : DEFAULT_LOCALE;
  });

  const setLocale = useCallback((lang) => {
    if (!translations[lang]) return;
    setLocaleState(lang);
    localStorage.setItem("portfolio-locale", lang);
  }, []);

  /**
   * t(key) — translation helper.
   * Returns the nested value for a dot-notation key, e.g. t("nav.home").
   * Returns the key itself as fallback if missing.
   */
  const t = useCallback(
    (key) => {
      const keys = key.split(".");
      let val = translations[locale];
      for (const k of keys) {
        if (val == null) return key;
        val = val[k];
      }
      return val ?? key;
    },
    [locale]
  );

  return (
    <AppContext.Provider value={{ theme, toggleTheme, locale, setLocale, t }}>
      {children}
    </AppContext.Provider>
  );
}

// Custom hook for easy consumption
export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
